import React, { useState, useEffect, useRef } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import './Testimonials.css';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [direction, setDirection] = useState(0);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const testimonials = [
    {
      id: 1,
      text: "The one-on-one sessions made a huge difference for my son. His math grades improved within a month, and he actually enjoys studying now!",
      author: "Parent of Grade 8 Student",
      rating: 5,
      subject: "Mathematics"
    },
    {
      id: 2,
      text: "I was nervous about online tutoring, but the lessons feel just like having a teacher in the room. My tutor explains everything so clearly!",
      author: "Ayesha, Grade 10 Student",
      rating: 5,
      subject: "Science"
    },
    {
      id: 3,
      text: "Great value for money. The personalized approach is exactly what my daughter needed to catch up in science.",
      author: "Mrs. Patel, Parent",
      rating: 5,
      subject: "Science"
    },
    {
      id: 4,
      text: "My son was really struggling with math, but after just a few weeks of classes here, he's so much more confident. He even looks forward to his sessions now!",
      author: "Parent of a Grade 7 student",
      rating: 5,
      subject: "Mathematics"
    },
    {
      id: 5,
      text: "Super flexible and convenient. I can book lessons around my daughter's schedule, and the tutors are patient and friendly.",
      author: "Mrs. Kapoor, Parent",
      rating: 5,
      subject: "General"
    },
    {
      id: 6,
      text: "The best part is how personal it feels. My tutor knows exactly where I get stuck and explains it in simple steps. Huge help before exams.",
      author: "Henry, Grade 12",
      rating: 5,
      subject: "A-Level"
    }
  ];

  // Auto-play functionality with progress
  useEffect(() => {
    if (!isAutoPlaying) {
      setProgress(0);
      return;
    }
    
    const duration = 3500; // 3.5 seconds
    const updateInterval = 50; // Update every 50ms for smooth animation
    const increment = (updateInterval / duration) * 100;
    
    const progressInterval = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = prevProgress + increment;
        if (newProgress >= 100) {
          setDirection(1);
          setCurrentIndex((prevIndex) => 
            prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
          );
          return 0; // Reset progress
        }
        return newProgress;
      });
    }, updateInterval);

    return () => clearInterval(progressInterval);
  }, [isAutoPlaying, testimonials.length, currentIndex]);

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setProgress(0);
    setDirection(1);
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
    // Resume auto-play after 10 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setProgress(0);
    setDirection(-1);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
    // Resume auto-play after 10 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setProgress(0);
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
    // Resume auto-play after 10 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  // Simple slide animation - much more performant
  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 300 : -300,
      opacity: 0
    })
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8, x: 50 },
    visible: {
      opacity: 1,
      scale: 1,
      x: 0,
      transition: { duration: 1, ease: "easeOut", delay: 0.5 }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section className="testimonials section" id="testimonials" ref={sectionRef}>
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">
            What Our Students <span className="gradient-text">Say</span>
          </h2>
          <p className="section-description">
            Real stories from students and parents who've experienced our personalized tutoring
          </p>
        </div>

        <div className="testimonials-slideshow">
          <div className="slideshow-content">
            <div className="testimonial-slider">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
                className="testimonial-card active-slide"
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={(event, info) => {
                  const swipeThreshold = 50;
                  if (info.offset.x > swipeThreshold) {
                    prevSlide();
                  } else if (info.offset.x < -swipeThreshold) {
                    nextSlide();
                  }
                }}
              >
                <div className="quote-icon">
                  <Quote className="quote" />
                </div>
                
                <div className="stars">
                  {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                    <Star key={i} className="star filled" />
                  ))}
                </div>
                
                <p className="testimonial-text">"{testimonials[currentIndex].text}"</p>
                
                <div className="testimonial-footer">
                  <div className="author-info">
                    <div className="author-name">{testimonials[currentIndex].author}</div>
                    <div className="subject-tag">{testimonials[currentIndex].subject}</div>
                  </div>
                </div>
              </motion.div>

              {/* Simple Navigation Controls */}
              <div className="slider-controls">
                <button className="slider-btn prev" onClick={prevSlide}>
                  <ChevronLeft />
                </button>
                <div className="play-pause-container">
                  <CircularProgressbar
                    value={isAutoPlaying ? progress : 0}
                    styles={buildStyles({
                      pathColor: '#2aa3ff',
                      trailColor: 'rgba(42, 163, 255, 0.1)',
                      strokeLinecap: 'round',
                      pathTransitionDuration: 0.1,
                    })}
                    strokeWidth={6}
                  />
                  <button className="slider-btn play-pause" onClick={toggleAutoPlay}>
                    {isAutoPlaying ? <Pause /> : <Play />}
                  </button>
                </div>
                <button className="slider-btn next" onClick={nextSlide}>
                  <ChevronRight />
                </button>
              </div>

              {/* Dots Indicator */}
              <div className="slider-dots">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`dot ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Student Image Section */}
          <div className="student-visual">
            <motion.div
              className="student-image-container"
              variants={imageVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              <motion.div
                className="floating-elements"
                variants={floatingVariants}
                animate="animate"
              >
                <div className="floating-element element-1">
                  <Star className="element-icon" />
                  <span>"I actually get it now!"</span>
                </div>
                <div className="floating-element element-2">
                  <Quote className="element-icon" />
                  <span>"Maths is fun!"</span>
                </div>
                <div className="floating-element element-3">
                  <Star className="element-icon" />
                  <span>"I can do this!"</span>
                </div>
              </motion.div>

              <motion.img
                src="/school-boy.png"
                alt="Happy student"
                className="student-image"
                variants={imageVariants}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              />

              <div className="image-glow"></div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
